import { AddCard, CardGrid, ContentCard, IconButton } from "@op/sf-editor";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { languageMap } from "../../../../../utils/language";
import { useState } from "react";
import { useDisable } from "../../../../../hooks/useDisable";
import { languageKeys } from "@op/puck-types";
import { getLanguageTopics } from "@/queries/language";
import z from "zod";
import { TopicCreateDialog } from "@/components/app/project/TopicCreateDialog";
import { FeatherSettings } from "@subframe/core";

const paramSchema = z.object({
  organizationSlug: z.string(),
  projectSlug: z.string(),
  languageKey: z.enum(languageKeys),
});

export const Route = createFileRoute(
  "/app/$organizationSlug/$projectSlug/$languageKey/",
)({
  params: paramSchema,
  loader: async ({
    params: { organizationSlug, projectSlug, languageKey },
  }) => {
    const languageTopics = await getLanguageTopics(
      organizationSlug,
      projectSlug,
      languageKey,
    );
    return { languageTopics };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { organizationSlug, projectSlug, languageKey } = Route.useParams();
  const [topicCreateDialogOpen, setTopicCreateDialogOpen] =
    useState<boolean>(false);
  const { languageTopics } = Route.useLoaderData();
  const { t } = useLingui();

  const queryClient = useQueryClient();

  const { data } = useQuery(
    {
      queryKey: [
        "editor-projects-topics",
        { organizationSlug, projectSlug, languageKey },
      ],
      initialData: languageTopics,
      queryFn: () =>
        getLanguageTopics(organizationSlug, projectSlug, languageKey),
    },
    queryClient,
  );

  const addTopicButtonDisabled = useDisable(organizationSlug, projectSlug, [
    "editor",
    "publisher",
    "admin",
  ]);

  return (
    <div className="flex h-full w-full max-w-[1280px] flex-col items-start gap-8 px-4 mx-auto mt-12">
      <div className="flex w-full flex-col items-start gap-6">
        <div className="flex flex-col items-start">
          <span className="text-caption font-caption text-subtext-color">
            {data?.organizationName} - {data?.projectName} -{" "}
            {data?.languageKey ? languageMap[data.languageKey] : ""}
          </span>
          <span className="text-heading-1 font-heading-1 text-default-font">
            <Trans>Topics</Trans>
          </span>
        </div>
        <CardGrid>
          {data?.topics.map((topic) => (
            <ContentCard
              key={topic.id}
              settingsSlot={
                <Link
                  to="/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug/settings"
                  params={{
                    organizationSlug,
                    projectSlug,
                    languageKey,
                    topicSlug: topic.slug,
                  }}
                  className="contents"
                >
                  <IconButton
                    variant="brand-tertiary"
                    size="medium"
                    icon={<FeatherSettings />}
                  />
                </Link>
              }
            >
              <Link
                to="/app/$organizationSlug/$projectSlug/$languageKey/$topicSlug"
                params={{
                  organizationSlug,
                  projectSlug,
                  languageKey,
                  topicSlug: topic.slug,
                }}
                className="contents"
              >
                <ContentCard.Label>
                  <Trans>Topic</Trans>
                </ContentCard.Label>
                <ContentCard.Title>{topic.name}</ContentCard.Title>
              </Link>
            </ContentCard>
          ))}
          {!addTopicButtonDisabled && (
            <AddCard
              label={t`Add a new topic...`}
              onClick={() => setTopicCreateDialogOpen(true)}
            />
          )}
        </CardGrid>
      </div>
      <TopicCreateDialog
        open={topicCreateDialogOpen}
        onOpenChange={setTopicCreateDialogOpen}
        organizationSlug={organizationSlug}
        projectSlug={projectSlug}
        languageKey={languageKey}
      />
    </div>
  );
}
